/* eslint-disable react/require-default-props, no-param-reassign */
import { Box, Flex, Icon, Text, Image, Spinner, useToast } from '@chakra-ui/core';
import Joi from 'joi';
import isError from 'lodash/isError';
import React, { memo, useCallback, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BiImageAdd } from 'react-icons/bi';
import { VideoEffectStore } from '../store/videoEffectModel';
import { withEffectCardWrapper } from '../withEffectCardWrapper';

const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png'] as const;
const ACCEPTED_EXTENSIONS = '.jpg,.jpeg,.png';
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

const fileSchema = Joi.object({
  type: Joi.string()
    .valid(...ACCEPTED_MIME_TYPES)
    .required(),
  size: Joi.number().max(MAX_FILE_SIZE_BYTES).required(),
}).unknown(true);

type UploadState = { phase: 'idle' } | { phase: 'uploading'; objectUrl: string };

const UploadPlaceholder = memo(function UploadPlaceholder({ onClick, isDisabled }: { onClick: () => void; isDisabled: boolean }) {
  const { t } = useTranslation();

  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      display="flex"
      alignItems="center"
      justifyContent="center"
      w="100%"
      h="100%"
      borderRadius="md"
      bg="gray.50"
      cursor="pointer"
      transition="all 0.15s ease"
      _hover={{ borderColor: 'blue.400', bg: 'blue.50' }}
      _focus={{ outline: 'none', boxShadow: '0 0 0 3px rgba(66,153,225,0.5)' }}
      _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
      aria-label={t('customBackground.uploadAriaLabel', 'Upload custom background')}
    >
      <Flex direction="column" align="center" gap="4px">
        <Icon as={BiImageAdd} color="gray.500" width="40px" height="40px" />
        <Text fontSize="11px" fontWeight="500" color="gray.500">
          {t('customBackground.label', 'Custom')}
        </Text>
      </Flex>
    </Box>
  );
});

export const AddCustomBackground = withEffectCardWrapper(
  memo(function AddCustomBackground() {
    const { t } = useTranslation('videoInterviewEffect');
    const toast = useToast();
    const inputRef = useRef<HTMLInputElement>(null);
    const uploadBackgroundImage = VideoEffectStore.useStoreActions((state) => state.uploadBackgroundImage);

    const [state, setState] = useState<UploadState>({ phase: 'idle' });

    const revokeIfPresent = useCallback((url?: string) => {
      // istanbul ignore else
      if (url) URL.revokeObjectURL(url);
    }, []);

    useEffect(() => {
      return () => {
        if ('objectUrl' in state) {
          revokeIfPresent(state.objectUrl);
        }
      };
    }, [state, revokeIfPresent]);

    const showError = useCallback(
      (message: string) => {
        toast({
          title: t('errors.uploadFailed'),
          description: message,
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'top',
        });
      },
      [t, toast],
    );

    const validateFile = useCallback(
      (file: File): string | null => {
        const { error } = fileSchema.validate({ type: file.type, size: file.size }, { abortEarly: true });

        if (!error) return null;

        const key = error.details[0]?.type ?? /* istanbul ignore next */ '';

        if (key === 'any.only') {
          return t('errors.fileTypeError');
        }
        // istanbul ignore else
        if (key === 'number.max') {
          return t('errors.fileSizeError');
        }
        // istanbul ignore next
        return t('errors.invalidFile');
      },
      [t],
    );

    const openFilePicker = useCallback(() => {
      inputRef.current?.click();
    }, []);

    const handleFileChange = useCallback(
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';

        if (!file) return;

        const errorMsg = validateFile(file);
        if (errorMsg) {
          showError(errorMsg);
          return;
        }

        const objectUrl = URL.createObjectURL(file);
        setState({ phase: 'uploading', objectUrl });

        const formData = new FormData();
        formData.append('file', file);

        try {
          await uploadBackgroundImage({ data: formData });

          revokeIfPresent(objectUrl);
          setState({ phase: 'idle' });
        } catch (err) {
          revokeIfPresent(objectUrl);
          setState({ phase: 'idle' });
          showError(isError(err) ? err.message : t('errors.saveError'));
        }
      },
      [validateFile, showError, uploadBackgroundImage, t, revokeIfPresent],
    );

    const isIdle = state.phase === 'idle';
    const isUploading = state.phase === 'uploading';

    return (
      <Box w="100%" h="100%">
        <Box
          as="input"
          ref={inputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS}
          onChange={handleFileChange}
          display="none"
          aria-hidden="true"
          tabIndex={-1}
        />

        {isIdle && <UploadPlaceholder onClick={openFilePicker} isDisabled={false} />}

        {isUploading && (
          <Box position="relative" w="100%" h="100%" borderRadius="md" overflow="hidden">
            <Image src={state.objectUrl} alt={t('customBackgroundEffects.previewAlt')} w="100%" h="100%" objectFit="cover" />
            <Flex
              position="absolute"
              inset="0"
              bg="blackAlpha.600"
              align="center"
              justify="center"
              borderRadius="md"
              aria-live="polite"
              aria-label={t('customBackgroundEffects.uploading')}
            >
              <Spinner color="white" size="md" />
            </Flex>
          </Box>
        )}
      </Box>
    );
  }),
);
